import { motion } from "framer-motion";
import { useRef, useState } from "react";
import axios from "axios";
import { Play, Pause, RotateCcw, Lock, Mail, Loader2 } from "lucide-react";
import { MEDITATION } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const ease = [0.22, 1, 0.36, 1];

export const AudioPlayer = () => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  const [unlocked, setUnlocked] = useState(
    () => localStorage.getItem("radiate_unlocked") === "1"
  );
  const [email, setEmail] = useState("");
  const [gate, setGate] = useState("idle"); // idle | loading | error
  const [gateErr, setGateErr] = useState("");

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play().catch(() => {});
      setPlaying(true);
    }
  };

  const restart = () => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = 0;
    setCur(0);
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = pct * dur;
    setCur(pct * dur);
  };

  const unlock = async (e) => {
    e.preventDefault();
    if (gate === "loading") return;
    if (!email.trim()) {
      setGate("error");
      setGateErr("Please enter your email to unlock.");
      return;
    }
    setGate("loading");
    try {
      await axios.post(`${API}/leads`, {
        email: email.trim(),
        source: "meditation_page",
      });
      localStorage.setItem("radiate_unlocked", "1");
      setUnlocked(true);
      setTimeout(() => {
        const a = audioRef.current;
        if (a) {
          a.play().catch(() => {});
          setPlaying(true);
        }
      }, 150);
    } catch (err) {
      setGate("error");
      setGateErr(
        err?.response?.status === 422
          ? "Please enter a valid email address."
          : "Something went wrong. Please try again."
      );
    }
  };

  const progress = dur ? (cur / dur) * 100 : 0;

  return (
    <section
      id="meditation"
      className="relative z-10 mx-auto max-w-5xl px-6 py-28 md:py-36"
      data-testid="meditation-section"
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease }}
        className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/[0.03] p-8 backdrop-blur-2xl md:p-14"
        data-testid="free-meditation-audio-player"
      >
        <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-2 md:gap-12">
          <div className="relative mx-auto w-full max-w-[320px] overflow-hidden rounded-[1.6rem] border border-white/12 shadow-[0_20px_60px_-20px_rgba(124,58,237,0.55)]">
            <img
              src={MEDITATION.cover}
              alt="Tap into Lucky Energy cover art"
              className="block h-auto w-full"
            />
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.35em] text-white/50">
              Free guided meditation
            </p>
            <p className="text-sm uppercase tracking-[0.2em] text-white/45">
              {MEDITATION.meta}
            </p>
            <p className="mt-5 max-w-md text-base leading-relaxed text-white/60">
              A taste of Radiate. Unlock “{MEDITATION.title}” with your email,
              then press play and let it tune you into a luckier, more magnetic
              state.
            </p>

            {unlocked ? (
              <div className="mt-8 flex items-center gap-5">
                <button
                  onClick={toggle}
                  className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-white text-black transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(255,255,255,0.4)] md:h-[4.5rem] md:w-[4.5rem]"
                  data-testid="play-meditation-button"
                  aria-label={playing ? "Pause" : "Play"}
                >
                  {playing ? (
                    <Pause className="h-6 w-6 fill-current" />
                  ) : (
                    <Play className="h-6 w-6 translate-x-[2px] fill-current" />
                  )}
                </button>

                <div className="flex-1">
                  <div
                    onClick={seek}
                    className="group relative h-2 cursor-pointer rounded-full bg-white/12"
                    data-testid="audio-progress-bar"
                  >
                    <div
                      className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-[#ffb7b2] to-[#f9d3b4]"
                      style={{ width: `${progress}%` }}
                    />
                    <div
                      className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white opacity-0 shadow transition-opacity group-hover:opacity-100"
                      style={{ left: `${progress}%` }}
                    />
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs tabular-nums text-white/50">
                    <span data-testid="audio-current-time">{fmt(cur)}</span>
                    <span data-testid="audio-duration">{fmt(dur)}</span>
                  </div>
                </div>

                <button
                  onClick={restart}
                  className="hidden h-12 w-12 shrink-0 place-items-center rounded-full border border-white/15 text-white/70 transition-colors hover:bg-white/10 hover:text-white sm:grid"
                  data-testid="audio-restart-button"
                  aria-label="Restart"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <form onSubmit={unlock} className="mt-8" data-testid="meditation-unlock-form">
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <Lock className="h-4 w-4 text-[#ffb7b2]" />
                  Enter your email to unlock this meditation
                </div>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  <div className="relative flex-1">
                    <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (gate === "error") setGate("idle");
                      }}
                      placeholder="you@email.com"
                      className="w-full rounded-full border border-white/15 bg-white/5 py-3.5 pl-11 pr-4 text-sm text-white placeholder:text-white/35 outline-none transition-colors focus:border-white/40"
                      data-testid="meditation-email-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={gate === "loading"}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-medium text-black transition-transform duration-300 hover:scale-105 disabled:opacity-70"
                    data-testid="meditation-unlock-button"
                  >
                    {gate === "loading" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Unlock & listen"
                    )}
                  </button>
                </div>
                {gate === "error" && (
                  <p className="mt-3 text-xs text-[#ffb7b2]" data-testid="meditation-unlock-error">
                    {gateErr}
                  </p>
                )}
              </form>
            )}
          </div>
        </div>

        <audio
          ref={audioRef}
          src={MEDITATION.audio}
          preload="metadata"
          onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
          onTimeUpdate={(e) => setCur(e.currentTarget.currentTime)}
          onEnded={() => setPlaying(false)}
        />
      </motion.div>
    </section>
  );
};
